import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  entriesRef,
  query,
  where,
  orderBy,
  subscribeToQuery,
  executeQuery,
} from '../firebase/firestore';

/**
 * useUserEntries
 *
 * Fetches user's entries for a specific raffle.
 *
 * Security Notes:
 * - Firestore rules ensure users can only read their own entries
 * - Entry data includes ticket numbers assigned by server
 */
export function useUserEntries(raffleId) {
  const { user, isAuthenticated } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.uid || !raffleId) {
      setEntries([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Subscribe to real-time updates for user's entries
    const q = query(
      entriesRef,
      where('userId', '==', user.uid),
      where('raffleId', '==', raffleId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = subscribeToQuery(q, (results) => {
      setEntries(results);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAuthenticated, user?.uid, raffleId]);

  // Calculate totals
  const totalTickets = entries.reduce((sum, entry) => sum + (entry.ticketCount || 0), 0);
  const ticketNumbers = entries.flatMap((entry) => entry.ticketNumbers || []);

  return {
    entries,
    totalTickets,
    ticketNumbers,
    loading,
    error,
    hasEntries: entries.length > 0,
  };
}

/**
 * useAllUserEntries
 *
 * Fetches all entries for the current user across all raffles.
 * Used for "My Entries" page.
 */
export function useAllUserEntries(options = {}) {
  const { status } = options; // 'active' | 'completed' | 'all'
  const { user, isAuthenticated } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setEntries([]);
      setLoading(false);
      return;
    }

    const fetchEntries = async () => {
      setLoading(true);
      setError(null);

      try {
        const q = query(
          entriesRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const results = await executeQuery(q);
        setEntries(results);
      } catch (err) {
        console.error('Error fetching user entries:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, [isAuthenticated, user?.uid, status]);

  return {
    entries,
    loading,
    error,
    totalEntries: entries.length,
  };
}

/**
 * useEntryBySession
 *
 * Fetches an entry by its Stripe session ID.
 * Used on EntrySuccess page after Stripe redirect.
 *
 * Polls for the entry since webhook may not have processed yet.
 */
export function useEntryBySession(sessionId, raffleId) {
  const { user, isAuthenticated } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!sessionId || !raffleId) {
      setError('Missing session information');
      setLoading(false);
      return;
    }

    if (!isAuthenticated || !user?.uid) {
      // Wait for auth to load
      return;
    }

    let isMounted = true;
    let pollCount = 0;
    const maxPolls = 10; // 10 attempts (10 seconds)
    const pollInterval = 1000; // 1 second
    let timeoutId = null;

    const fetchEntry = async () => {
      try {
        const q = query(
          entriesRef,
          where('stripeSessionId', '==', sessionId),
          where('userId', '==', user.uid)
        );

        const results = await executeQuery(q);

        if (results.length > 0 && isMounted) {
          setEntry(results[0]);
          setLoading(false);
          return true; // Found entry
        }
        return false; // Not found yet
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching entry:', err);
          // Don't set error on query failure, just retry
        }
        return false;
      }
    };

    const poll = async () => {
      if (!isMounted) return;

      const found = await fetchEntry();
      if (found || !isMounted) return;

      pollCount++;
      if (pollCount < maxPolls) {
        timeoutId = setTimeout(poll, pollInterval);
      } else {
        // Not found after max attempts - may need webhook setup
        setNotFound(true);
        setLoading(false);
      }
    };

    poll();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId, raffleId, isAuthenticated, user?.uid]);

  return {
    entry,
    loading,
    error,
    notFound,
    ticketNumbers: entry?.ticketNumbers || [],
    ticketCount: entry?.ticketCount || 0,
  };
}

/**
 * Entry data structure in Firestore:
 *
 * entries/{entryId}
 * {
 *   userId: "user123",
 *   raffleId: "raffle456",
 *   ticketCount: 3,
 *   ticketNumbers: [12, 13, 14],
 *   amountPaid: 1500, // cents
 *   stripePaymentIntentId: "pi_xxx",
 *   stripeCheckoutSessionId: "cs_xxx",
 *   status: "completed", // pending | completed | refunded
 *   refundStatus: null, // null | pending | completed
 *   createdAt: Timestamp,
 *   updatedAt: Timestamp,
 * }
 */
